import React from "react";
import { Button, Descriptions, Drawer, Space, Tag, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CopyableText from "components/common/CopyableText";

const resolveDiscountType = (coupon) => coupon?.discountType || coupon?.valueType;
const resolveDiscountValue = (coupon) =>
  coupon?.discountValue ?? coupon?.value ?? coupon?.amount;
const resolveStartsAt = (coupon) =>
  coupon?.startsAt || coupon?.validFrom || coupon?.effectiveFrom;
const resolveExpiresAt = (coupon) =>
  coupon?.expiresAt || coupon?.validUntil || coupon?.effectiveTo;
const resolveRestrictedUserIds = (coupon) =>
  coupon?.restrictedUserIds ||
  coupon?.eligibleUserIds ||
  coupon?.allowedUserIds ||
  [];

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return `${numericAmount.toFixed(2)} ${currency || "USD"}`;
  }
};

const formatDiscount = (coupon) => {
  const discountType = resolveDiscountType(coupon);
  const discountValue = Number(resolveDiscountValue(coupon));
  if (!Number.isFinite(discountValue)) return "-";

  if (discountType === "PERCENTAGE") {
    const displayValue = discountValue <= 1 ? discountValue * 100 : discountValue;
    return `${displayValue.toFixed(2)}%`;
  }

  return formatCurrency(discountValue, coupon?.currency);
};

const formatDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm A") : "-");
const formatStatus = (value) => String(value || "-").toUpperCase();

export default function CouponDetailsDrawer({ open, coupon, onClose, onEdit }) {
  const restrictedUserIds = resolveRestrictedUserIds(coupon);
  const isRestricted =
    coupon?.restrictedToUsers === true || restrictedUserIds.length > 0;

  return (
    <Drawer
      className="coupon-details-drawer"
      placement="right"
      width={440}
      open={open}
      title="Coupon Details"
      onClose={onClose}
      destroyOnHidden
      zIndex={9998}
      extra={onEdit ? (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          disabled={!coupon}
          onClick={() => onEdit?.(coupon)}
        >
          Edit
        </Button>
      ) : null}
      styles={{
        header: { padding: "12px 16px" },
        body: { padding: 16 },
      }}
    >
      <style>
        {`
          .coupon-details-drawer .ant-drawer-title {
            font-size: 14px;
            font-weight: 700;
          }
          .coupon-details-drawer .ant-descriptions-item-label,
          .coupon-details-drawer .ant-descriptions-item-content {
            font-size: 12px;
          }
        `}
      </style>

      {coupon ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div>
            <Typography.Title level={5} style={{ marginBottom: 4 }}>
              {coupon.name || "-"}
            </Typography.Title>
            <Typography.Text type="secondary">
              {coupon.description || "No description"}
            </Typography.Text>
          </div>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Code">
              <CopyableText value={coupon.code} />
            </Descriptions.Item>
            <Descriptions.Item label="Discount">
              {formatDiscount(coupon)}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              {formatCurrency(coupon.amount ?? coupon.balance, coupon.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={formatStatus(coupon.status) === "ACTIVE" ? "cyan" : "red"}>
                {formatStatus(coupon.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Availability">
              <Tag color={isRestricted ? "purple" : "blue"}>
                {isRestricted ? "Restricted" : "Shareable"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={formatStatus(coupon.type) === "SYSTEM" ? "geekblue" : "default"}>
                {formatStatus(coupon.type || "USER")}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Product">
              {coupon.clientApp || coupon.app || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Charge Code">
              {coupon.chargeCode || coupon.chargeItemCode || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">
              {coupon.currency || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Minimum Amount">
              {formatCurrency(coupon.minChargeAmount ?? coupon.minAmount, coupon.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Max Discount">
              {formatCurrency(coupon.maxDiscountAmount, coupon.currency)}
            </Descriptions.Item>
            <Descriptions.Item label="Usage Limit">
              {coupon.usageLimit || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Per User Limit">
              {coupon.perUserLimit || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Stackable">
              {coupon.stackable ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {formatStatus(coupon.duration)}
            </Descriptions.Item>
            <Descriptions.Item label="Starts">
              {formatDate(resolveStartsAt(coupon))}
            </Descriptions.Item>
            <Descriptions.Item label="Expires">
              {formatDate(resolveExpiresAt(coupon))}
            </Descriptions.Item>
            {isRestricted ? (
              <Descriptions.Item label="Allowed Users">
                {restrictedUserIds.length}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Space>
      ) : null}
    </Drawer>
  );
}
